import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dimensions,
  FlatList,
  StyleSheet,
  Text,
  View,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "react-native";
import LinearGradient from "react-native-linear-gradient";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import { DivineBackground } from "../../shared/ui/DivineBackground";
import { AppButton } from "../../shared/ui/AppButton";
import { PageDots } from "../../shared/ui/PageDots";
import { Colors } from "../../../theme/colors";
import { Spacing } from "../../../theme/spacing";
import { Typography } from "../../../theme/typography";
import { Storage } from "../../services/storage/storage";
import { StorageKeys } from "../../services/storage/keys";
import { AuthStackParamList } from "../../navigation/navigationTypes";

const { width, height } = Dimensions.get("window");

type Nav = NativeStackNavigationProp<AuthStackParamList, "Onboarding">;

type Slide = {
  id: string;
  title: string;
  description: string;
  image: any; // require
};

const SLIDES: Slide[] = [
  {
    id: "1",
    title: "Welcome to Your Spiritual Journey",
    description:
      "Connect with the divine energy of Sri Sri Radha Krishna.\nStart your day with blessings and devotion.",
    image: require("../../../assets/Slide1.jpg"),
  },
  {
    id: "2",
    title: "Guided by the Guru",
    description:
      "Follow the path shown by Srila Prabhupada. Access daily\nreading, hearing, and chanting resources.",
    image: require("../../../assets/Slide2.jpg"),
  },
  {
    id: "3",
    title: "Track Your Sadhana",
    description:
      "Maintain your daily spiritual practices including Mangla\nAarti, Chanting, and more with ease.",
    image: require("../../../assets/Slide3.jpg"),
  },
];

export function OnboardingScreen() {
  const navigation = useNavigation<Nav>();
  const listRef = useRef<FlatList<Slide>>(null);
  const insets = useSafeAreaInsets();

  const [index, setIndex] = useState(0);

  useEffect(() => {
    (async () => {
      const seen = await Storage.get(StorageKeys.hasSeenOnboarding);
      if (seen === "true") navigation.replace("Login");
    })();
  }, [navigation]);

  const current = useMemo(() => SLIDES[index], [index]);

  const onMomentumEnd = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const newIndex = Math.round(e.nativeEvent.contentOffset.x / width);
    setIndex(newIndex);
  };

  const goTo = (i: number) => {
    const clamped = Math.max(0, Math.min(i, SLIDES.length - 1));
    setIndex(clamped);
    listRef.current?.scrollToOffset({ offset: clamped * width, animated: true });
  };

  const handleContinue = async () => {
    await Storage.set(StorageKeys.hasSeenOnboarding, "true");
    navigation.replace("Login");
  };

  return (
    <DivineBackground backgroundImage={current.image}>
      {/* Horizontal pager */}
      <FlatList
        ref={listRef}
        data={SLIDES}
        keyExtractor={(item) => item.id}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={onMomentumEnd}
        renderItem={() => <View style={{ width, height }} />}
      />

      {/* Bottom overlay */}
      <View style={StyleSheet.absoluteFill}>
        <View style={{ flex: 1 }} />

        <LinearGradient
          colors={[
            "transparent",
            "rgba(74,35,17,0.55)",
            "rgba(74,35,17,0.92)",
          ]}
          locations={[0, 0.45, 1]}
          style={[
            styles.bottom,
            // ✅ lift content above footer + system nav bar
            { paddingBottom: Spacing.xl + Math.max(insets.bottom, 10) + 28 },
          ]}
        >
          <View style={styles.textWrap}>
            <Text style={styles.title}>{current.title}</Text>
            <Text style={styles.desc}>{current.description}</Text>
          </View>

          {/* ✅ better spacing */}
          <View style={{ height: 16 }} />

          <PageDots total={SLIDES.length} index={index} />

          <View style={{ height: 14 }} />

          <View style={styles.btnRow}>
            <AppButton
              title="Previous"
              variant="ghost"
              onPress={() => goTo(index - 1)}
              style={index === 0 ? styles.invisible : undefined}
            />

            {index === SLIDES.length - 1 ? (
              <AppButton
                title="Get Started"
                variant="primary"
                onPress={handleContinue}
                style={styles.getStarted}
              />
            ) : (
              <AppButton
                title="Next"
                variant="light"
                onPress={() => goTo(index + 1)}
              />
            )}
          </View>

          <View style={{ height: 6 }} />
        </LinearGradient>
      </View>
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  bottom: {
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.xl,
  },
  textWrap: {
    alignItems: "center",
    paddingHorizontal: 8,
  },
  title: {
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Typography.serif,
    fontWeight: "700",
    color: Colors.white,
    textAlign: "center",
    textShadowColor: "rgba(0,0,0,0.25)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 8,
  },
  desc: {
    marginTop: 10,
    fontSize: 16,
    lineHeight: 24,
    fontFamily: Typography.sans,
    color: Colors.orangeText,
    textAlign: "center",
    maxWidth: 520,
    textShadowColor: "rgba(0,0,0,0.18)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  btnRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 6,
  },
  getStarted: {
    paddingHorizontal: 26,
    backgroundColor: Colors.orange500,
  },
  invisible: {
    opacity: 0,
  },
});
